import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import api from '../lib/api';

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_powerline-booking/artifacts/co7mv4rs_IslandCoastalElectric-MountainOnly-Logo.V3-VECTOR.png";

const SetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await api.setPassword(token, password);
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to set password. Token may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-primary-dark flex items-center justify-center p-4">
        <div className="text-center">
          <p className="text-white/60">Invalid link. No token provided.</p>
          <Link to="/admin" className="text-electric-teal mt-4 inline-block">Go to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary-dark flex items-center justify-center p-4" data-testid="set-password-page">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <img src={LOGO_ICON} alt="Island Coastal Electric" className="h-16 w-auto mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white">Set Your Password</h1>
          <p className="text-white/60 mt-2">Create a password for your admin account</p>
        </div>

        <div className="bg-secondary-dark p-8 rounded-lg border border-white/10">
          {success ? (
            <div className="text-center py-4" data-testid="set-password-success">
              <CheckCircle className="w-12 h-12 text-green-400 mx-auto mb-4" />
              <p className="text-white font-medium mb-2">Password Set Successfully!</p>
              <p className="text-white/60 text-sm mb-6">You can now log in with your email and password.</p>
              <Link to="/admin">
                <Button className="bg-electric-teal text-primary-dark font-bold w-full">Go to Login</Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="set-password-form">
              {error && (
                <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 text-red-400 text-sm" data-testid="set-password-error">
                  {error}
                </div>
              )}
              <div>
                <Label className="text-white mb-2 block">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-primary-dark border-white/20 text-white pl-10"
                    placeholder="Minimum 8 characters"
                    required
                    data-testid="new-password-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white mb-2 block">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                  <Input
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="bg-primary-dark border-white/20 text-white pl-10"
                    placeholder="Re-enter your password"
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-electric-teal text-primary-dark font-bold uppercase tracking-wider py-4"
                data-testid="set-password-submit"
              >
                {loading ? 'Setting Password...' : 'Set Password'}
              </Button>
              <p className="text-white/30 text-xs text-center mt-3">
                Or <Link to="/admin" className="text-electric-teal hover:underline">sign in with Google</Link> instead.
              </p>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default SetPasswordPage;
